import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, CheckCircle } from 'lucide-react';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { authToast } from '@/lib/toast/authToast';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const resetPasswordSchema = z.object({
  email: z.string().min(1, 'E-mail is required').email('Please enter a valid email address'),
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  const { t } = useTranslation('common');
  const { resetPassword } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
  });

  const { formState } = form;
  const { isValid } = formState;

  const onSubmit = async (data: ResetPasswordFormValues) => {
    try {
      setIsSubmitting(true);
      setResetError(null);
      setResetSuccess(false);

      // Call Firebase Auth password reset
      await resetPassword(data.email);

      // Success - show toast and success state
      setResetSuccess(true);
      authToast.resetEmailSent();
      logger.log('Password reset email sent:', { email: data.email });
    } catch (error: any) {
      // Handle Firebase Auth errors
      let errorMessage = t('auth.resetPassword.errors.generic');

      if (error.code === 'auth/user-not-found') {
        errorMessage = t('auth.resetPassword.errors.userNotFound');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('auth.resetPassword.errors.invalidEmailFormat');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t('auth.resetPassword.errors.tooManyRequests');
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = t('auth.resetPassword.errors.networkError');
      }

      setResetError(errorMessage);
      logger.error('Password reset error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidSubmit = () => {
    setHasSubmitError(true);
  };

  const isButtonDisabled = !isValid || isSubmitting || resetSuccess;

  return (
    <AuthLayout>
      <Card
        className={cn(
          'rounded-2xl shadow-lg border-border transition-transform',
          'motion-reduce:transition-none',
          hasSubmitError && 'animate-shake'
        )}
      >
        <CardContent className="pt-8 pb-6 px-8">
          <AuthHeader
            title={t('auth.resetPassword.title')}
            subtitle={t('auth.resetPassword.subtitle')}
          />

          {resetSuccess ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                <div className="text-sm text-green-800 dark:text-green-200">
                  {t('auth.resetPassword.success')} {t('auth.resetPassword.checkInbox')}
                </div>
              </div>
              <Link to="/login">
                <Button variant="outline" className="w-full h-11">
                  {t('auth.resetPassword.backToLogin')}
                </Button>
              </Link>
            </div>
          ) : (
            <Form {...form}>
              <form
                action="#"
                onSubmit={form.handleSubmit(onSubmit, handleInvalidSubmit)}
                className="space-y-4"
                aria-busy={isSubmitting}
                noValidate
              >
                {/* Email Field */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        {t('auth.resetPassword.email')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t('auth.resetPassword.emailPlaceholder')}
                          autoComplete="email"
                          autoFocus
                          disabled={isSubmitting}
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage aria-live="polite" />
                    </FormItem>
                  )}
                />

                {/* Reset Error Message */}
                {resetError && (
                  <div
                    className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3"
                    role="alert"
                    aria-live="assertive"
                  >
                    {resetError}
                  </div>
                )}

                {/* Send Reset Link Button */}
                <Button
                  type="submit"
                  disabled={isButtonDisabled}
                  className="w-full h-11 text-base font-medium mt-6"
                  style={{
                    backgroundColor: !isButtonDisabled ? '#CDB4F8' : undefined,
                    color: !isButtonDisabled ? '#000000' : undefined,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('auth.resetPassword.submitting')}
                    </>
                  ) : (
                    t('auth.resetPassword.submit')
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 px-8 pb-8">
          <Separator />

          {/* Back to Sign In Link */}
          <div className="text-center text-sm text-muted-foreground">
            {t('auth.resetPassword.rememberPassword')}{' '}
            <Link
              to="/login"
              className="font-medium hover:underline"
              style={{ color: '#CDB4F8' }}
              tabIndex={isSubmitting ? -1 : 0}
            >
              {t('auth.signIn.submit')}
            </Link>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground pt-2">
            {t('auth.signIn.footer')}
          </div>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
